import { useCallback, useEffect, useMemo } from 'react'

import { useSelector } from 'react-redux'

import {
  getHasPaidCampaign,
  getHasOnePaidCampaign,
  usePaidCampaignsCountQuery,
} from 'src/Redux/metadataApi'
import {
  EXPIRED_TIMER_VALUE,
  getCheckoutTimer,
  getIsTimerExpired,
  startCountdown,
  updateCountdown,
} from 'src/Redux/checkoutCountdown-process'
import {
  useGetArtistFeatureFlagsQuery,
  useUpdateArtistFeatureFlagsMutation,
} from 'src/Redux/A-BFeaturesApi'
import { getIsUserReferral } from 'src/Redux/auth-process/userSlice/selectors'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

import { useBudgetRange } from '../Redux/spotifyBudgetRangeApi'

import { useAppDispatch } from './redux'
import {
  EXPIRED_TIMER_FEATURE_VALUE,
  QUICK_CHECKOUT_FEATURE_NAME,
} from './useCheckoutTimerFeratureFlag'

const TIMEOUT = 1000
const COUNTDOWN_VALUE = 420
const MIN_CHECKOUT_BONUS_BUDGET = 60
const DISABLED_QUICK_CHECKOUT_VARIATION = 0

interface UseBonusTooltipTimerInterface {
  isNextTimeBonus: boolean
  isNextTimeBonusReceived: boolean
  startNextTimeBonusCountdown: () => void
}

export const useBonusTooltipTimer = ({
  platform,
  budget,
}: {
  platform: CampaignPlatformTypeEnum
  budget?: number | null
}): UseBonusTooltipTimerInterface => {
  const dispatch = useAppDispatch()

  const { data: spotifyPaidData } = usePaidCampaignsCountQuery(
    CampaignPlatformTypeEnum.SPOTIFY,
    {
      refetchOnMountOrArgChange: true,
    },
  )
  const { data: tiktokPaidData } = usePaidCampaignsCountQuery(
    CampaignPlatformTypeEnum.TIKTOK,
    {
      refetchOnMountOrArgChange: true,
    },
  )
  const { isLowReachError } = useBudgetRange()
  const hasAnyPaidCampaign =
    getHasPaidCampaign(spotifyPaidData) || getHasPaidCampaign(tiktokPaidData)
  const currentPlatformPaidData =
    platform === CampaignPlatformTypeEnum.SPOTIFY
      ? spotifyPaidData
      : tiktokPaidData
  const isUserReferral = useSelector(getIsUserReferral)
  const countdown = useSelector(getCheckoutTimer)
  const isTimerExpired = useSelector(getIsTimerExpired)

  const [addFeatureForUser] = useUpdateArtistFeatureFlagsMutation()

  const { data: quickCheckoutVariation } = useGetArtistFeatureFlagsQuery(
    void 0,
    {
      selectFromResult: (result) => ({
        ...result,
        data: result?.data?.features?.find(
          (item) => item?.name === QUICK_CHECKOUT_FEATURE_NAME,
        )?.variation,
      }),
    },
  )

  const isBelowBonusThreshold =
    typeof budget === 'number' && budget < MIN_CHECKOUT_BONUS_BUDGET

  const isQuickCheckoutDisabled =
    quickCheckoutVariation === DISABLED_QUICK_CHECKOUT_VARIATION

  const isBonusDisqualified = isBelowBonusThreshold || isQuickCheckoutDisabled

  const isNextTimeBonus = useMemo(
    () =>
      !isUserReferral &&
      !hasAnyPaidCampaign &&
      !isLowReachError &&
      !isBonusDisqualified,
    [isUserReferral, hasAnyPaidCampaign, isLowReachError, isBonusDisqualified],
  )

  const isNextTimeBonusVisible =
    isNextTimeBonus && countdown !== null && !isTimerExpired

  const isNextTimeBonusReceived =
    getHasOnePaidCampaign(currentPlatformPaidData) && !isUserReferral

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>
    if (isNextTimeBonus && !isTimerExpired) {
      timer = setInterval(() => {
        if (countdown === 0 && !isTimerExpired) {
          addFeatureForUser({
            featureName: QUICK_CHECKOUT_FEATURE_NAME,
            variation: EXPIRED_TIMER_FEATURE_VALUE,
          })
          localStorage.setItem(
            'quick_checkout',
            String(EXPIRED_TIMER_FEATURE_VALUE),
          )
          clearInterval(timer)
          dispatch(updateCountdown(EXPIRED_TIMER_VALUE))
        } else {
          countdown &&
            !isTimerExpired &&
            dispatch(updateCountdown(countdown - 1))
        }
      }, TIMEOUT)
    }

    return () => clearInterval(timer)
  }, [addFeatureForUser, countdown, dispatch, isNextTimeBonus, isTimerExpired])

  const startNextTimeBonusCountdown = useCallback((): void => {
    if (isNextTimeBonus) {
      localStorage.removeItem(QUICK_CHECKOUT_FEATURE_NAME)
      dispatch(startCountdown(COUNTDOWN_VALUE))
    }
  }, [dispatch, isNextTimeBonus])

  return {
    isNextTimeBonus: isNextTimeBonusVisible,
    isNextTimeBonusReceived,
    startNextTimeBonusCountdown,
  }
}
